import React, { useContext, useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import { useNavigate } from 'react-router-dom';
import {
  CYCLE_LABEL,
  CYCLE_YEAR,
  DEFAULT_GRAD_YEAR,
  SELECTABLE_GRAD_YEARS,
} from '../../config/recruitingCycle';
import { GlobalContext } from '../../contexts/GlobalContext';
import { buildQuestPlan } from '../../utils/questPlan';
import BouncingImage from '../BouncingImage';
import QuestTimeline from './QuestTimeline';

const AcceptQuestModal = ({ isOpen, onAccept }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const [formData, setFormData] = useState({
    gradYear: DEFAULT_GRAD_YEAR,
    emailUpdates: true,
    startDate: 'now',
    hoursPerWeek: 5,
  });
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const { questPlan, setQuestPlan, completedNodes, nodes } = useContext(GlobalContext);
  // Loading messages and their durations (total 10 seconds)
  const loadingSteps = [
    { message: 'Assessing your goals...', duration: 2000 },
    { message: 'Generating interview practice schedule...', duration: 2000 },
    { message: 'Finding relevant job postings...', duration: 2000 },
    { message: 'Creating your custom roadmap...', duration: 2000 },
    { message: 'Doing cartwheels...', duration: 2000 },
  ];
  // set is opem to true upon rendering
  useEffect(() => {
    isOpen = true;
  }, []);

  // Handle window resize for confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Trigger fade-in animation when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setOpacity(0);
      // Start fade in after a small delay
      setTimeout(() => {
        setOpacity(1);
      }, 50);
    } else {
      setOpacity(0);
      setTimeout(() => {
        setIsVisible(false);
      }, 500); // Wait for fade out before hiding
    }
  }, [isOpen]);

  const onClose = () => {
    // Navigate home
    navigate('/');
  };

  // Create quest plan. Shared with quest renewal so the two cannot drift.
  const createQuestPlan = () =>
    buildQuestPlan({
      gradYear: formData.gradYear,
      hoursPerWeek: formData.hoursPerWeek,
      nodes,
      completedNodes,
    });

  // Handle form data changes
  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Move to next step with fade transition
  const nextStep = () => {
    setIsFading(true);
    setOpacity(0);

    // Use a longer transition (2 seconds) when moving to the final step
    const transitionTime = currentStep + 1 === 5 ? 2000 : 500;

    setTimeout(() => {
      setCurrentStep(currentStep + 1);
      setIsFading(false);
      setOpacity(1);
    }, transitionTime);
  };

  // Move to previous step
  const prevStep = () => {
    setIsFading(true);
    setOpacity(0);
    setTimeout(() => {
      setCurrentStep(currentStep - 1);
      setIsFading(false);
      setOpacity(1);
    }, 500);
  };

  // Handle final submission
  const handleSubmit = () => {
    setLoadingProgress(0);
    setLoadingMessage(loadingSteps[0].message);
    setCurrentStep(4); // Go to step 4 (loading step)
    const myQuestPlan = createQuestPlan();
    setQuestPlan(myQuestPlan);
    console.log(questPlan);
    let currentStepIndex = 0;
    let totalProgress = 0;
    const totalDuration = 10000; // 10 seconds total

    const progressInterval = setInterval(() => {
      totalProgress += 10; // Update every 10ms
      const progressPercentage = Math.min(Math.floor((totalProgress / totalDuration) * 100), 100);
      setLoadingProgress(progressPercentage);

      // Check if we need to move to the next loading message
      const currentStepEndTime = loadingSteps
        .slice(0, currentStepIndex + 1)
        .reduce((sum, step) => sum + step.duration, 0);
      if (totalProgress >= currentStepEndTime && currentStepIndex < loadingSteps.length - 1) {
        currentStepIndex++;
        setLoadingMessage(loadingSteps[currentStepIndex].message);
      }

      // When loading is complete
      if (progressPercentage >= 100) {
        clearInterval(progressInterval);
        // Add fade transition to step 5
        setOpacity(0);
        setTimeout(() => {
          setCurrentStep(5);
          setOpacity(1);
        }, 500);
      }
    }, 10);
  };

  // Render step 1: Initial question
  const renderStep1 = () => (
    <div className="flex items-center justify-center h-full">
      <div className="text-center max-w-2xl mx-auto p-8">
        <div className="flex justify-center mb-6">
          <BouncingImage width="15" imageLink="/badges/kitty_excite.png" />
        </div>
        <h2 className="text-5xl font-bold text-black mb-6">
          🗺️ Accept InternHub's{' '}
          <span className="text-blue-500 text-6xl">{CYCLE_LABEL} Quest! </span>
        </h2>
        <p className="text-xl text-gray-700 mb-5 max-w-xl mx-auto">
          <span className="text-black font-bold">
            Is your goal to land a summer {CYCLE_YEAR} internship?
          </span>{' '}
          Start your journey here - and gain access to a personalized practice plan, weekly
          check-ins and tasks, emails with opportunities, and more!
        </p>
        <div className="flex justify-center mb-4">
          <button
            onClick={nextStep}
            className="px-8 py-3 bg-linear-to-r from-blue-500 to-pink-400 text-white rounded-xl text-xl font-medium hover:from-blue-600 hover:to-pink-500 transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-md active:shadow-inner"
          >
            Learn more!
          </button>
        </div>
        <p className="text-gray-500 italic cursor-pointer hover:underline" onClick={onClose}>
          I'm not interested in a summer {CYCLE_YEAR} internship
        </p>
      </div>
    </div>
  );

  // Render step 2: Quest introduction
  const renderStep2 = () => (
    <div className="flex items-center justify-center h-full">
      <div className="text-center max-w-2xl mx-auto p-8">
        <div className="flex justify-center mb-6">
          <BouncingImage
            width="15"
            imageLink={
              !showConfetti ? '/badges/kitty_scientist.png' : '/badges/kitty_celebrate.png'
            }
          />
        </div>
        <h2 className="text-4xl font-bold text-black mb-6">💎 Accepting the quest will unlock:</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 max-w-3xl mx-auto">
          <div className="bg-blue-50 p-4 rounded-xl shadow-xs border border-blue-100 hover:shadow-md transition-shadow">
            <div className="text-3xl mb-2">📅</div>
            <h3 className="font-semibold text-lg mb-1">Custom preparation plan</h3>
            <p className="text-gray-700">Personalized roadmap for the next year</p>
          </div>
          <div className="bg-green-50 p-4 rounded-xl shadow-xs border border-green-100 hover:shadow-md transition-shadow">
            <div className="text-3xl mb-2">📧</div>
            <h3 className="font-semibold text-lg mb-1">Weekly email updates</h3>
            <p className="text-gray-700">Regular check-ins and progress tracking</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-xl shadow-xs border border-purple-100 hover:shadow-md transition-shadow">
            <div className="text-3xl mb-2">❓</div>
            <h3 className="font-semibold text-lg mb-1">Question hotline</h3>
            <p className="text-gray-700">24-hour response time for your questions</p>
          </div>
          <div className="bg-amber-50 p-4 rounded-xl shadow-xs border border-amber-100 hover:shadow-md transition-shadow">
            <div className="text-3xl mb-2">🎮</div>
            <h3 className="font-semibold text-lg mb-1">Fun application process</h3>
            <p className="text-gray-700">Enjoyable journey to your dream internship</p>
          </div>
        </div>
        {/* <p className="text-xl text-gray-700 mb-8 font-medium">
                    So, are you ready to land a summer {CYCLE_YEAR} internship?
                </p> */}
        <div className="flex flex-col items-center space-y-4 max-w-md mx-auto">
          <button
            type="button"
            className={`w-full py-4 px-8 inline-flex justify-center items-center gap-x-2 text-lg font-semibold rounded-lg border border-transparent ${showConfetti ? 'bg-linear-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 focus:from-green-600 focus:to-green-700' : 'bg-linear-to-r from-blue-500 to-pink-400 text-white hover:from-blue-600 hover:to-pink-500 focus:from-blue-600 focus:to-pink-500'} disabled:opacity-50 disabled:pointer-events-none transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-md active:shadow-inner`}
            onClick={() => {
              setShowConfetti(true);
              setTimeout(() => {
                nextStep();
              }, 2000);
            }}
          >
            {showConfetti ? "Let's go!" : `I want a summer ${CYCLE_YEAR} internship!`}
          </button>
          {showConfetti && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 1000,
                pointerEvents: 'none',
              }}
            >
              <Confetti
                width={windowSize.width}
                height={windowSize.height}
                recycle={false}
                numberOfPieces={200}
                gravity={0.3}
              />
            </div>
          )}
          <button
            type="button"
            className="text-gray-600 hover:text-gray-800 hover:underline text-sm font-medium transition-colors"
            onClick={() => {
              nextStep();
            }}
          >
            <p className="text-gray-500 italic cursor-pointer hover:underline" onClick={onClose}>
              Not now, but I can change my mind later
            </p>
          </button>
        </div>
      </div>
    </div>
  );
  // Render step 3: Quest benefits
  const renderStep3 = () => (
    <div className="flex h-full items-center justify-center">
      <div className="max-w-2xl mx-auto p-4">
        <div className="flex items-center mb-6 justify-center">
          <div className="flex flex-col">
            <h3 className="text-4xl font-bold text-gray-900 text-center">✏️ Create your quest</h3>
          </div>
        </div>

        {/* Graduation year selection */}
        <div className="mb-6 border border-gray-200 p-5 rounded-xl shadow-xs hover:shadow-md transition-shadow">
          <h4 className="text-lg font-semibold text-gray-800 mb-2">
            What is your graduation year?
          </h4>
          <p className="text-sm text-gray-600 italic mb-3">
            This helps us tailor your internship search timeline.
          </p>
          <div className="flex flex-wrap gap-3">
            {SELECTABLE_GRAD_YEARS.map((year) => (
              <label key={year} className="inline-flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="gradYear"
                  className="form-radio h-4 w-4 text-blue-600"
                  checked={formData.gradYear === year}
                  onChange={() => handleChange('gradYear', year)}
                />
                <span className="ml-2 text-sm">{year}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Email updates preference */}
        <div className="mb-6 border border-gray-200 p-5 rounded-xl shadow-xs hover:shadow-md transition-shadow">
          <h4 className="text-lg font-semibold text-gray-800 mb-2">
            Enable weekly email updates & check-ins
          </h4>
          <p className="text-sm text-gray-600 italic mb-3">
            Strongly recommended to keep you on track! You can disable this anytime.
          </p>
          <div className="flex items-center space-x-4">
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                className="form-radio h-4 w-4 text-blue-600"
                checked={formData.emailUpdates}
                onChange={() => handleChange('emailUpdates', true)}
              />
              <span className="ml-2 text-sm">Yes, please!</span>
            </label>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                className="form-radio h-4 w-4 text-blue-600"
                checked={!formData.emailUpdates}
                onChange={() => handleChange('emailUpdates', false)}
              />
              <span className="ml-2 text-sm">No, thanks</span>
            </label>
          </div>
        </div>

        {/* Start date selection */}
        <div className="mb-6 border border-gray-200 p-5 rounded-xl shadow-xs hover:shadow-md transition-shadow">
          <h4 className="text-lg font-semibold text-gray-800 mb-2">
            When would you like to begin your quest?
          </h4>
          <p className="text-sm text-gray-600 italic mb-3">
            This is when your email updates + weekly tasks will begin!
          </p>
          <div className="flex flex-col space-y-2 mb-2">
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                className="form-radio h-4 w-4 text-blue-600"
                checked={formData.startDate === 'now'}
                onChange={() => handleChange('startDate', 'now')}
              />
              <span className="ml-2 text-sm">Start right away (it's never too early!)</span>
            </label>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                className="form-radio h-4 w-4 text-blue-600"
                checked={formData.startDate === 'future'}
                onChange={() => handleChange('startDate', 'future')}
              />
              <span className="ml-2 text-sm">Start on a later date:</span>
            </label>
          </div>

          <div className="mb-1 mt-2">
            <input
              type="date"
              className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 ${formData.startDate !== 'future' ? 'opacity-50 cursor-not-allowed' : ''}`}
              min={new Date().toISOString().split('T')[0]}
              disabled={formData.startDate !== 'future'}
              onChange={(e) =>
                formData.startDate === 'future' && handleChange('futureDate', e.target.value)
              }
            />
          </div>
        </div>

        {/* Hours per week */}
        <div className="mb-6 border border-gray-200 p-5 rounded-xl shadow-xs hover:shadow-md transition-shadow">
          <h4 className="text-lg font-semibold text-gray-800 mb-2">
            How many hours per week can you dedicate to your quest?
          </h4>
          <p className="text-sm text-gray-600 italic mb-3">Adjust anytime! 5 hours recommended.</p>

          <div className="mb-2">
            <span className="text-lg font-bold text-gray-800">
              {formData.hoursPerWeek} hours per week
            </span>
          </div>

          <input
            type="range"
            min="1"
            max="10"
            value={formData.hoursPerWeek}
            onChange={(e) => handleChange('hoursPerWeek', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-600 mt-1">
            <span>1 hr</span>
            <span>5 hrs</span>
            <span>10 hrs</span>
          </div>
        </div>

        <div className="flex items-center justify-center mt-8">
          <button
            onClick={handleSubmit}
            className="px-10 py-5 bg-linear-to-r from-blue-500 to-pink-400 text-white rounded-xl text-xl font-medium hover:from-blue-600 hover:to-pink-500 transition-colors shadow-md transform hover:scale-105 active:scale-95"
          >
            Generate quest plan! 🚀
          </button>
        </div>
      </div>
    </div>
  );

  // Render step 4: Loading step
  const renderStep4 = () => (
    <div className="flex items-center justify-center h-full">
      <div className="text-center max-w-2xl mx-auto p-8">
        <div className="flex flex-row justify-center items-center mb-8">
          <BouncingImage width="5" imageLink="/badges/kitty_writing.png" />
          <h2 className="text-4xl font-bold text-black mb-8 ml-5">Generating your plan...</h2>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
          <div
            className="h-4 rounded-full transition-all duration-300 ease-in-out"
            style={{
              width: `${loadingProgress}%`,
              background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)',
            }}
          ></div>
        </div>

        <p className="text-xl text-gray-700 mb-2 italic">{loadingMessage}</p>
      </div>
    </div>
  );

  // Render step 5: Recruitment Timeline
  const renderStep5 = () => <QuestTimeline formData={formData} questPlan={questPlan} />;

  // Render the current step
  const renderCurrentStep = () => {
    if (!isVisible) return null;

    const stepContent = (() => {
      switch (currentStep) {
        case 1:
          return renderStep1();
        case 2:
          return renderStep2();
        case 3:
          return renderStep3();
        case 4:
          return renderStep4();
        case 5:
          return renderStep5();
        default:
          return renderStep1();
      }
    })();

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-white">
        <div
          style={{
            opacity: opacity,
            transition: `opacity ${currentStep === 1 && !isFading ? 2000 : 500}ms ease-in-out`,
            height: '100%',
            width: '100%',
          }}
        >
          {stepContent}
        </div>
      </div>
    );
  };

  return renderCurrentStep();
};

export default AcceptQuestModal;
